
import { AssessmentResult, FileData, LlamaCppConfig } from "../types";
import { extractTextFromFile } from "../utils/fileParser";

const DEFAULT_ASSESSMENT: Partial<AssessmentResult> = {
  overallMark: 0,
  minMark: 0,
  maxMark: 0,
  classification: "Fail",
  criteriaResults: [],
  generalStrengths: [],
  areasForImprovement: [],
  summaryFeedback: "No summary feedback provided."
};

const ensureArray = (val: any): string[] => {
  if (Array.isArray(val)) return val.map(v => String(v));
  if (typeof val === 'string' && val.trim()) return [val];
  return [];
};

export const fetchLlamaCppModels = async (baseUrl: string): Promise<string[]> => {
  try {
    const response = await fetch(`${baseUrl}/v1/models`, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache'
    });
    if (!response.ok) return [];
    const data = await response.json();
    return data.data?.map((m: any) => m.id) || [];
  } catch (err) {
    console.error("Error fetching llama.cpp models:", err);
    return [];
  }
};

export const testLlamaCppConnection = async (baseUrl: string): Promise<boolean> => {
  try {
    const response = await fetch(`${baseUrl}/v1/models`, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache'
    });
    return response.ok;
  } catch (err) {
    return false;
  }
};

export const assessWithLlamaCpp = async (
  rubricFile: FileData,
  assignmentFile: FileData,
  config: LlamaCppConfig,
  extraContext: string = ''
): Promise<AssessmentResult> => {
  const rubricText = await extractTextFromFile(rubricFile.base64, rubricFile.mimeType);
  const assignmentText = await extractTextFromFile(assignmentFile.base64, assignmentFile.mimeType);
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const systemPrompt = `
    You are a Senior Academic in Pharmacy and Pharmaceutical Sciences at the University of Nottingham.
    Evaluate the following student work against the provided rubric with academic judgement.

    Today's date is ${today}.
    IMPORTANT: Current date context is ${today}. Only flag references as "in the future" if they are dated strictly AFTER ${today}.

    TECHNICAL CONSTRAINTS:
    - You are reading a TEXT-ONLY extraction.
    - RUBRIC INTERPRETATION: The rubric may be a text extraction from a table or a structured JSON object. If JSON, map 'criteria' to 'criteriaResults'. The 'marks' field in JSON represents the maximum marks for that criterion.
    - VISUAL CONTENT: If a criterion mentions Figures/Diagrams/Structures, award a neutral mark, set isVisual: true for that criterion, and state visual evidence was not verified.

    UK MARKING SCALES (NOTTINGHAM):
    - 70%+: 1st | 60-69%: 2:1 | 50-59%: 2:2 | 40-49%: 3rd | <40%: Fail

    ${extraContext ? `ADDITIONAL ACADEMIC CONTEXT FOR THIS ASSESSMENT:\n${extraContext}\n` : ''}

    RESPONSE FORMAT:
    Return ONLY a valid JSON object with:
    overallMark, minMark, maxMark, classification, criteriaResults (array of objects with: criterion, mark, maxMark, feedback, isVisual), generalStrengths (array of strings), areasForImprovement (array of strings), and summaryFeedback.
  `;

  const userPrompt = `
    RUBRIC:
    ${rubricText}

    STUDENT WORK (TEXT EXTRACTION):
    ${assignmentText}
  `;

  try {
    const payload: any = {
      model: config.model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ],
      temperature: 0.1,
      response_format: { type: 'json_object' }
    };

    const response = await fetch(`${config.baseUrl}/v1/chat/completions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      mode: 'cors',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => 'No error body');
      throw new Error(`llama.cpp API Error (${response.status}): ${errorBody}`);
    }

    const data = await response.json();
    let content = data.choices?.[0]?.message?.content;
    if (!content) {
      throw new Error("Invalid response format from llama.cpp.");
    }

    // Handle think tags (some models emit <｜end▁of▁thinking｜> markup)
    if (content.includes('</think>')) {
      content = content.split('</think>').pop()?.trim() || content;
    }
    if (content.includes('```json')) {
      content = content.split('```json')[1].split('```')[0].trim();
    }

    const rawResult = JSON.parse(content);
    const criteriaResults = Array.isArray(rawResult.criteriaResults) ? rawResult.criteriaResults : [];

    const totalEarned = criteriaResults.reduce((sum: number, c: any) => sum + (Number(c.mark) || 0), 0);
    let totalMax = criteriaResults.reduce((sum: number, c: any) => sum + (Number(c.maxMark) || 0), 0);

    if (totalMax === 0 && rawResult.maxMark) {
      totalMax = Number(rawResult.maxMark);
    }

    const overallMark = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : (Number(rawResult.overallMark) || 0);

    const result: AssessmentResult = {
      ...DEFAULT_ASSESSMENT,
      ...rawResult,
      criteriaResults,
      totalEarned,
      totalMax,
      overallMark,
      generalStrengths: ensureArray(rawResult.generalStrengths),
      areasForImprovement: ensureArray(rawResult.areasForImprovement)
    } as AssessmentResult;

    // Adjusted Mark (excluding visual criteria)
    const nonVisual = result.criteriaResults.filter(c => !c.isVisual);
    if (nonVisual.length > 0 && nonVisual.length < result.criteriaResults.length) {
      const earned = nonVisual.reduce((sum, c) => sum + (Number(c.mark) || 0), 0);
      const possible = nonVisual.reduce((sum, c) => sum + (Number(c.maxMark) || 0), 0);
      result.adjustedMark = possible > 0 ? Math.round((earned / possible) * 100) : result.overallMark;
    } else {
      result.adjustedMark = result.overallMark;
    }

    result.fileName = assignmentFile.name;
    return result;
  } catch (err: any) {
    console.error("llama.cpp Error:", err);
    throw new Error(`llama.cpp Assessment Failed: ${err.message}`);
  }
};
